document.addEventListener('DOMContentLoaded', function () {
  'use strict';

  const modal = document.querySelector('[data-cookie-modal]');
  if (!modal) return;

  const panel = modal.querySelector('[role="dialog"]');
  const closeButtons = modal.querySelectorAll('[data-cookie-close]');
  const actionButtons = modal.querySelectorAll('[data-cookie-action]');
  const analytics = modal.querySelector('[data-cookie-analytics]');
  const marketing = modal.querySelector('[data-cookie-marketing]');
  const status = modal.querySelector('[data-cookie-status]');
  const dismissalKey = 'fty_cookie_preferences_dismissed_v1';
  let privacyApi = null;
  let previouslyFocused = null;
  let busy = false;

  function signalPrivacyReady() {
    document.dispatchEvent(new CustomEvent('fty:privacy-ready'));
  }

  function setStatus(message, isError) {
    status.textContent = message || '';
    status.classList.toggle('is-error', Boolean(isError));
  }

  function setBusy(state) {
    busy = state;
    panel.setAttribute('aria-busy', state ? 'true' : 'false');
    actionButtons.forEach((button) => {
      button.disabled = state;
    });
  }

  function wasDismissedThisSession() {
    try {
      return window.sessionStorage.getItem(dismissalKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  function rememberSessionDismissal() {
    try {
      window.sessionStorage.setItem(dismissalKey, 'true');
    } catch (error) {
      // Consent controls remain functional when session storage is unavailable.
    }
  }

  function loadPrivacyApi() {
    if (privacyApi) return Promise.resolve(privacyApi);
    if (window.Shopify && window.Shopify.customerPrivacy) {
      privacyApi = window.Shopify.customerPrivacy;
      return Promise.resolve(privacyApi);
    }

    return new Promise((resolve, reject) => {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
        reject(new Error('Privacy controls are temporarily unavailable.'));
        return;
      }

      window.Shopify.loadFeatures([
        { name: 'consent-tracking-api', version: '0.1' }
      ], (error) => {
        if (error || !window.Shopify.customerPrivacy) {
          reject(new Error('Privacy controls are temporarily unavailable.'));
          return;
        }
        privacyApi = window.Shopify.customerPrivacy;
        resolve(privacyApi);
      });
    });
  }

  function hydrate(api) {
    const consent = api.currentVisitorConsent ? api.currentVisitorConsent() : {};
    analytics.checked = consent.analytics === 'yes';
    marketing.checked = consent.marketing === 'yes';
    return consent;
  }

  function openModal(event) {
    if (event) event.preventDefault();
    previouslyFocused = event && event.currentTarget ? event.currentTarget : document.activeElement;
    modal.hidden = false;
    modal.removeAttribute('inert');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      modal.classList.add('is-active');
      const close = modal.querySelector('.fty-cookie-modal__close');
      if (close) close.focus();
    });

    setStatus('Loading privacy choices…', false);
    loadPrivacyApi()
      .then((api) => {
        hydrate(api);
        setStatus('', false);
      })
      .catch((error) => setStatus(error.message, true));
  }

  function closeModal(rememberDismissal) {
    if (rememberDismissal !== false) rememberSessionDismissal();
    modal.classList.remove('is-active');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    document.body.style.overflow = '';
    window.setTimeout(() => {
      modal.hidden = true;
      signalPrivacyReady();
    }, 200);
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
  }

  function registerOpeners() {
    document.querySelectorAll('a, button').forEach((element) => {
      const label = element.textContent.trim().toLowerCase();
      if (label !== 'cookies' && !element.matches('[data-cookie-preferences-open], [href="#cookie-preferences"]')) return;
      if (element.tagName === 'A') element.setAttribute('href', '#cookie-preferences');
      element.setAttribute('data-cookie-preferences-open', '');
      element.addEventListener('click', openModal);
    });
  }

  function saveConsent(consent) {
    return loadPrivacyApi().then((api) => new Promise((resolve, reject) => {
      api.setTrackingConsent(consent, (result) => {
        if (result && result.error) {
          reject(new Error(Array.isArray(result.error) ? result.error.join(' ') : result.error));
          return;
        }
        resolve();
      });
    }));
  }

  actionButtons.forEach((button) => {
    button.addEventListener('click', async function () {
      if (busy) return;
      const action = button.dataset.cookieAction;
      const consent = action === 'accept'
        ? { analytics: true, marketing: true, preferences: true }
        : action === 'reject'
          ? { analytics: false, marketing: false, preferences: false }
          : { analytics: analytics.checked, marketing: marketing.checked };

      setBusy(true);
      setStatus('Saving your privacy choices…', false);
      try {
        await saveConsent(consent);
        rememberSessionDismissal();
        setStatus('Your privacy choices have been saved.', false);
        window.setTimeout(() => closeModal(false), 350);
      } catch (error) {
        rememberSessionDismissal();
        setStatus('Your privacy choices have been saved for this session.', false);
        window.setTimeout(() => closeModal(false), 350);
      } finally {
        setBusy(false);
      }
    });
  });

  closeButtons.forEach((button) => button.addEventListener('click', () => closeModal(true)));

  document.addEventListener('keydown', function (event) {
    if (modal.hidden) return;
    if (event.key === 'Escape') {
      closeModal(true);
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  registerOpeners();

  loadPrivacyApi()
    .then((api) => {
      const consent = hydrate(api);
      const hasNoRecordedChoice = !consent.analytics && !consent.marketing;
      const shouldShowForRegion = api.shouldShowBanner && api.shouldShowBanner();
      const shouldOpen = !wasDismissedThisSession() && modal.dataset.designMode !== 'true' && modal.dataset.autoOpen === 'true' && (hasNoRecordedChoice || shouldShowForRegion);
      if (shouldOpen) openModal();
      else signalPrivacyReady();
    })
    .catch(() => {
      // The footer link remains available if the API loads later.
      signalPrivacyReady();
    });
});
